import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import precomputed from '../i18n/translations.json';

const LT_URL = 'https://api-toolsconduto.conduto.com.ec/candidatos/traducir';
const LT_API_KEY = '';
const SOURCE_LANG = 'es';
const TARGET_LANGS = ['en', 'pt'];
const STORAGE_KEY = 'lt_cache_v3';
const MAX_ITEMS = 25;
const MAX_CHARS = 2500;
const CONCURRENCY = 3;

const loadCache = () => {
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    stored = {};
  }
  const base = {};
  Object.entries(precomputed).forEach(([lang, dict]) => {
    Object.entries(dict).forEach(([text, value]) => {
      base[`${lang}|${text}`] = value;
    });
  });
  return { ...stored, ...base };
};

let cache = loadCache();
let pending = {};
let inflight = new Set();
let failed = new Set();
let timer = null;
let version = 0;
let activeLang = SOURCE_LANG;
const listeners = new Set();

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getVersion = () => version;

const getLoading = () => {
  for (const key of inflight) {
    if (key.startsWith(`${activeLang}|`)) {
      return true;
    }
  }
  return false;
};

const emit = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};

const persist = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    cache = { ...cache };
  }
};

const cacheKey = (lang, text) => `${lang}|${text}`;

const chunk = (texts) => {
  const groups = [];
  let current = [];
  let chars = 0;
  texts.forEach((text) => {
    if (current.length >= MAX_ITEMS || (chars + text.length > MAX_CHARS && current.length > 0)) {
      groups.push(current);
      current = [];
      chars = 0;
    }
    current.push(text);
    chars += text.length;
  });
  if (current.length > 0) {
    groups.push(current);
  }
  return groups;
};

const requestTranslation = async (texts, lang) => {
  const body = {
    q: texts.length === 1 ? texts[0] : texts,
    source: SOURCE_LANG,
    target: lang,
    format: 'text'
  };
  if (LT_API_KEY) {
    body.api_key = LT_API_KEY;
  }

  const response = await fetch(LT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

  const data = await response.json();

  if (!response.ok || data.error) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  return Array.isArray(data.translatedText) ? data.translatedText : [data.translatedText];
};

const processGroup = async (texts, lang) => {
  try {
    const translated = await requestTranslation(texts, lang);
    texts.forEach((text, i) => {
      if (translated[i]) {
        cache[cacheKey(lang, text)] = translated[i];
      } else {
        failed.add(cacheKey(lang, text));
      }
    });
    persist();
  } catch (error) {
    console.error('LibreTranslate error:', error.message);
    texts.forEach((text) => failed.add(cacheKey(lang, text)));
  } finally {
    texts.forEach((text) => inflight.delete(cacheKey(lang, text)));
    emit();
  }
};

const runPool = async (groups, lang) => {
  const queue = [...groups];
  const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
    while (queue.length > 0) {
      await processGroup(queue.shift(), lang);
    }
  });
  await Promise.all(workers);
};

const flush = async () => {
  timer = null;
  emit();
  const batches = pending;
  pending = {};

  const langs = Object.keys(batches).sort((a, b) => {
    if (a === activeLang) return -1;
    if (b === activeLang) return 1;
    return 0;
  });

  for (const lang of langs) {
    await runPool(chunk(Array.from(batches[lang])), lang);
  }
};

const enqueue = (text, lang) => {
  const key = cacheKey(lang, text);
  if (cache[key] || inflight.has(key) || failed.has(key)) {
    return;
  }
  inflight.add(key);
  if (!pending[lang]) {
    pending[lang] = new Set();
  }
  pending[lang].add(text);
  if (!timer) {
    timer = setTimeout(flush, 30);
  }
};

const translate = (text, lang) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  if (lang !== SOURCE_LANG) {
    enqueue(text, lang);
  }

  TARGET_LANGS.forEach((target) => {
    if (target !== lang) {
      enqueue(text, target);
    }
  });

  if (lang === SOURCE_LANG) {
    return text;
  }

  return cache[cacheKey(lang, text)] || text;
};

export const useTr = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || SOURCE_LANG).slice(0, 2);
  activeLang = lang;
  useSyncExternalStore(subscribe, getVersion);
  return (text) => translate(text, lang);
};

export const useTranslating = () => useSyncExternalStore(subscribe, getLoading);